// Performance tier detection (TAS-311).
//
// Field case, 2026-08-21: a budget Android opening the game inside
// WhatsApp's in-app browser dropped frames under the full animation load —
// while session replay recorded every DOM mutation those animations made.
// This module answers one question — "should this device get the full
// show?" — for both the animation vocabulary (apps/game/src/lib/anims.js)
// and the session-replay sampler (analytics-logger).
//
// Deliberately conservative: only signals with low false-positive rates.
// Manual override for testing: localStorage.tasted_perf = 'lite' | 'full'.

function nav() {
  return typeof navigator !== 'undefined' ? navigator : null;
}

// In-app browsers (WhatsApp/Instagram/Facebook webviews) throttle rAF and
// get less GPU memory than the real browser. Android WebView marks its UA
// with '; wv)' or 'Version/X.Y … Chrome'; the big in-app browsers add their
// own tokens; iOS webviews lack the Safari token.
export function isWebView() {
  const ua = nav()?.userAgent ?? '';
  if (!ua) return false;
  if (/; ?wv\)/.test(ua)) return true;                    // Android WebView
  if (/FBAN|FBAV|Instagram|Line\/|GSA\/|Snapchat/i.test(ua)) return true;
  // Legacy WebView shape ('Version/x.y … Chrome') — but Samsung Internet
  // and some real browsers carry the same pair, so exclude their tokens
  // before trusting it (Codex).
  if (/Android.*Version\/\d+\.\d+.*Chrome/.test(ua) && !/SamsungBrowser|EdgA|OPR|Firefox/.test(ua)) return true;
  if (/iPhone|iPad/.test(ua) && /AppleWebKit/.test(ua) && !/Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)) return true;
  return false;
}

// Low-end hardware. deviceMemory is the strongest signal, but Chrome rounds
// it DOWN to a power of two: a 6GB phone says 4, a 3GB phone says 2. So ≤4
// swept in the whole mid-range (prod 2476, 2026-10-02: no 3D bottle on phones
// that could draw it); ≤2 is the budget tier, real memory 3GB and under.
// hardwareConcurrency alone over-flags mid-rangers, so it only counts at ≤2.
export function isLowEndDevice() {
  const n = nav();
  if (!n) return false;
  // Brave randomises hardwareConcurrency and deviceMemory against
  // fingerprinting (often reporting 2), so neither says anything about the
  // phone there. Treat Brave as full unless something else flags it.
  if (n.brave) return false;
  if (typeof n.deviceMemory === 'number' && n.deviceMemory <= 2) return true;
  if (typeof n.hardwareConcurrency === 'number' && n.hardwareConcurrency <= 2) return true;
  return false;
}

let _cached = null;

/**
 * True → drop to the lite animation tier + skip replay capture.
 * The heuristic result is memoized; the localStorage override is read on
 * every call so setting `tasted_perf` from the console takes effect
 * immediately (Codex).
 */
export function isLitePerf() {
  try {
    // ?perf=full or ?perf=lite on any URL pins the tier on this phone, so a
    // tester can override it without a console.
    const param = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('perf') : null;
    if (param === 'full' || param === 'lite') {
      try { localStorage.setItem('tasted_perf', param); } catch { /* blocked storage: the param still counts for this page */ }
      return param === 'lite';
    }
    const override = typeof localStorage !== 'undefined' ? localStorage.getItem('tasted_perf') : null;
    if (override === 'lite') return true;
    if (override === 'full') return false;
  } catch { /* storage blocked — fall through to detection */ }
  if (_cached === null) _cached = isWebView() || isLowEndDevice();
  return _cached;
}

/** Test hook — clears the memo so mocked navigators re-evaluate. */
export function __resetPerfTierForTests() {
  _cached = null;
}
